/**
 * Back in Stock Notification System
 * Handles popup modal and Klaviyo API integration (Email/SMS)
 */

(function() {
  'use strict';

  // State
  let currentFormType = 'email';
  let isSubmitting = false;

  /**
   * Get popup elements (refreshed each time to handle dynamic content)
   */
  function getPopupElements() {
    return {
      popup: document.querySelector('[data-bis-popup]'),
      overlay: document.querySelector('[data-bis-overlay]'),
      closeBtn: document.querySelector('[data-bis-close]'),
      toggleBtns: document.querySelectorAll('[data-bis-toggle]'),
      form: document.querySelector('[data-bis-form]'),
      fields: document.querySelectorAll('[data-bis-field]'),
      signupTypeInput: document.querySelector('[data-bis-signup-type]'),
      variantDisplay: document.querySelector('[data-bis-variant-display]'),
      variantInputs: document.querySelectorAll('[data-bis-variant-input]')
    };
  }

  /**
   * Open the popup
   */
  function openPopup() {
    const { popup, overlay } = getPopupElements();
    if (!popup || !overlay) return;

    popup.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus the visible input
    setTimeout(() => {
      const visibleField = popup.querySelector(`[data-bis-field="${currentFormType}"]`);
      const input = visibleField?.querySelector('input');
      if (input) input.focus();
    }, 100);
  }

  /**
   * Close the popup
   */
  function closePopup() {
    const { popup, overlay, form } = getPopupElements();
    if (!popup) return;

    popup.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';

    // Reset form
    if (form) {
      form.reset();
      const message = form.querySelector('[data-bis-message]');
      if (message) {
        message.hidden = true;
        message.className = 'bis-popup__message';
      }
    }
  }

  /**
   * Toggle between Email and SMS input fields
   */
  function toggleForm(type) {
    const { popup, toggleBtns, fields, signupTypeInput } = getPopupElements();
    if (!popup) return;

    currentFormType = type;

    // Update toggle buttons
    toggleBtns.forEach(btn => {
      const btnType = btn.getAttribute('data-bis-toggle');
      const isActive = btnType === type;
      btn.classList.toggle('bis-toggle-btn--active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Show/hide input fields
    fields.forEach(field => {
      const fieldType = field.getAttribute('data-bis-field');
      field.hidden = fieldType !== type;
    });

    // Update hidden signup type
    if (signupTypeInput) {
      signupTypeInput.value = type;
    }

    // Focus input in active field
    const activeField = popup.querySelector(`[data-bis-field="${type}"]`);
    const input = activeField?.querySelector('input');
    if (input) input.focus();
  }

  /**
   * Update popup with variant data
   */
  function updateVariantData(variantId, variantTitle, productTitle) {
    const { popup, variantDisplay, variantInputs } = getPopupElements();
    if (!popup) return;

    // Update display text
    if (variantDisplay) {
      if (variantTitle && variantTitle !== 'Default Title') {
        variantDisplay.textContent = ` - ${variantTitle}`;
      } else {
        variantDisplay.textContent = '';
      }
    }

    // Update hidden variant inputs
    variantInputs.forEach(input => {
      input.value = variantId;
    });

    // Update popup data attributes
    if (variantId) popup.dataset.variantId = variantId;
    if (variantTitle) popup.dataset.variantTitle = variantTitle;
    if (productTitle) popup.dataset.productTitle = productTitle;
  }

  /**
   * Validate email format
   */
  function isValidEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Validate phone format (basic validation)
   */
  function isValidPhone(phone) {
    // Remove all non-numeric characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    // Must have at least 10 digits
    return cleaned.replace(/\D/g, '').length >= 10;
  }

  /**
   * Show message in form
   */
  function showMessage(form, message, isSuccess) {
    const messageEl = form.querySelector('[data-bis-message]');
    if (!messageEl) return;

    messageEl.textContent = message;
    messageEl.className = 'bis-popup__message';
    messageEl.classList.add(isSuccess ? 'bis-popup__message--success' : 'bis-popup__message--error');
    messageEl.hidden = false;
  }

  /**
   * Submit to Klaviyo Back in Stock API
   */
  async function submitToKlaviyo(formData) {
    // Get Klaviyo public API key from window or fallback
    const klaviyoApiKey = window.klaviyoPublicApiKey || window.themeVariables?.klaviyoApiKey || '';

    if (!klaviyoApiKey) {
      console.error('Klaviyo API key not configured');
      return { success: false, message: 'Notification service not configured. Please contact support.' };
    }

    const isEmail = formData.signup_type === 'email';
    // Client API requires company_id as query parameter
    const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoApiKey}`;

    // Build profile data based on signup type
    const profileAttributes = isEmail
      ? { email: formData.email }
      : { phone_number: formData.phone };

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: isEmail ? ['EMAIL'] : ['SMS'],
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${formData.variant_id}`
            }
          }
        }
      }
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-02-15'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok || response.status === 202) {
        return { success: true };
      }

      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.errors?.[0]?.detail || 'Failed to subscribe. Please try again.'
      };
    } catch (error) {
      console.error('Klaviyo API error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      };
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e) {
    e.preventDefault();

    if (isSubmitting) return;

    const form = e.target;
    const submitBtn = form.querySelector('[data-bis-submit]');
    const originalBtnText = submitBtn.textContent;

    // Get form data
    const formData = Object.fromEntries(new FormData(form).entries());

    // Validate based on current form type
    if (currentFormType === 'email') {
      if (!formData.email || !isValidEmail(formData.email)) {
        showMessage(form, 'Please enter a valid email address.', false);
        return;
      }
    } else {
      if (!formData.phone || !isValidPhone(formData.phone)) {
        showMessage(form, 'Please enter a valid phone number.', false);
        return;
      }
    }

    // Submit
    isSubmitting = true;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    const result = await submitToKlaviyo(formData);

    if (result.success) {
      showMessage(form, "You're on the list! We'll notify you when this item is back in stock.", true);
      form.reset();

      // Close popup after delay
      setTimeout(() => {
        closePopup();
      }, 3000);
    } else {
      showMessage(form, result.message, false);
    }

    isSubmitting = false;
    submitBtn.disabled = false;
    submitBtn.textContent = originalBtnText;
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Use event delegation for dynamically added elements
    document.addEventListener('click', (e) => {
      // Close button
      if (e.target.closest('[data-bis-close]')) {
        closePopup();
        return;
      }

      // Overlay click
      if (e.target.matches('[data-bis-overlay]')) {
        closePopup();
        return;
      }

      // Toggle buttons
      const toggleBtn = e.target.closest('[data-bis-toggle]');
      if (toggleBtn) {
        const type = toggleBtn.getAttribute('data-bis-toggle');
        toggleForm(type);
        return;
      }

      // BIS trigger buttons (open popup)
      const trigger = e.target.closest('[data-bis-trigger]');
      if (trigger) {
        e.preventDefault();
        const variantId = trigger.dataset.variantId;
        const variantTitle = trigger.dataset.variantTitle;
        const productTitle = trigger.dataset.productTitle;

        updateVariantData(variantId, variantTitle, productTitle);
        openPopup();
        return;
      }
    });

    // Form submissions - use event delegation
    document.addEventListener('submit', (e) => {
      if (e.target.matches('[data-bis-form]')) {
        handleSubmit(e);
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      const { popup } = getPopupElements();
      if (e.key === 'Escape' && popup?.classList.contains('is-open')) {
        closePopup();
      }
    });

    // Listen for variant changes to update BIS buttons and popup
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;

      // Update all BIS triggers with new variant data
      document.querySelectorAll('[data-bis-trigger]').forEach(trigger => {
        trigger.dataset.variantId = variant.id;
        trigger.dataset.variantTitle = variant.title || '';
      });

      // Update the popup if it's open
      const { popup } = getPopupElements();
      if (popup?.classList.contains('is-open')) {
        updateVariantData(variant.id, variant.title);
      }

      // Handle button visibility swap
      updateButtonVisibility(variant, e.target);
    });
  }

  /**
   * Update button visibility based on variant availability
   */
  function updateButtonVisibility(variant, form) {
    if (!form) return;

    const buyButtons = form.closest('product-form')?.querySelector('buy-buttons');
    if (!buyButtons) return;

    const bisButton = buyButtons.querySelector('[data-bis-trigger]');
    const atcButton = buyButtons.querySelector('button[type="submit"]:not([data-bis-trigger]), .buy-button2');

    if (!bisButton && !atcButton) return;

    const isAvailable = variant && variant.available;

    if (bisButton) {
      bisButton.style.display = isAvailable ? 'none' : '';
    }
    if (atcButton) {
      atcButton.style.display = isAvailable ? '' : 'none';
    }
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    setupEventListeners();
  }

  // Expose functions globally for external use
  window.BisPopup = {
    open: openPopup,
    close: closePopup,
    updateVariant: updateVariantData
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
